import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Link from '@mui/material/Link'
import LaunchIcon from '@mui/icons-material/Launch'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Theme } from '@mui/material/styles'
import { LocationLink } from '../LocationLink'
import { EntityLocationDTO } from '@user/store/map/typings'
import { googleMapsOpenInMapsLink } from '@user/components/HereMap/utils/googleMapsLinks'
import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useCheckPermissions } from '@user/store/authorization/permissions'
import { StatusSection } from './atoms/StatusSection'
import { ViewKnockStatusHistoryButton } from './atoms/ViewKnockStatusHistoryButton'
import { getPestPacDisplayName } from './utils/getPestPacDisplayName'
import { getTelHref } from './utils/getTelHref'
import { resolveKnockStatusHistoryLink } from './utils/resolveKnockStatusHistoryLink'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  applyClearedOverride,
  useClearKnockStatus,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { useGetLocationUrl } from '@user/store/authorization/hooks/useGetLocationUrl'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateKnockStatusHistoryById } from '@user/store/knockStatus/api'
import {
  getPestPacHistoryLink,
  invalidatePestPacHistoryLink,
  PestPacHistoryLink,
} from '@user/store/map/pestPacHistoryLink'
import { toast } from '@sales-center/components'

type MapBubbleContentsPestPacProps = {
  location: EntityLocationDTO
  onClose?: () => void
  showCloseBtn?: boolean
  onKnockStatusClick?: () => void
  onAddLeadClick?: () => void
  onViewClick?: () => void
  /** Re-fetches the map after the active knock status is cleared (SH-661). */
  onKnockStatusCleared?: () => void
  viewLabel?: string
  showViewChevron?: boolean
  /** Opens the history modal. Takes the resolved location id, which a cleared pin no longer carries. */
  onViewKnockStatusHistory?: (knockStatusLocationId: number) => void
}

export default function MapBubbleContentsPestPac({
  location,
  onClose,
  showCloseBtn,
  onKnockStatusClick,
  onAddLeadClick,
  onViewClick,
  onKnockStatusCleared,
  viewLabel = 'View',
  showViewChevron = true,
  onViewKnockStatusHistory,
}: MapBubbleContentsPestPacProps) {
  const details = location.pestPacDetails
  const isD2dEnabled = useD2DKnockEnabled()
  const [canAddLead] = useCheckPermissions('leads_add')
  const [canEdit] = useKnockStatusEditPermission('KnockStatus')

  const knockStatusLocationId = location.knockStatusLocationId ?? null
  const pestPacLocationId =
    location.pestPacLocationId ?? details?.locationId ?? null

  const handleKnockStatusCleared = useCallback(() => {
    // The history count just changed, so the cached history lookup for this location is stale.
    if (pestPacLocationId) invalidatePestPacHistoryLink(pestPacLocationId)
    onKnockStatusCleared?.()
  }, [pestPacLocationId, onKnockStatusCleared])

  // Standalone (external) PestPac pin: backend resolves 'Unknown' -> 'KnockStatus' (SH-851).
  const {
    clearConfirmOpen,
    isClearing,
    cleared,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  } = useClearKnockStatus({
    knockStatusLocationId,
    variant: '',
    entityType: 'Unknown',
    onRefresh: handleKnockStatusCleared,
  })

  const dispatch = useAppDispatch()
  const [isSavingReason, setIsSavingReason] = useState(false)

  const handleSaveReason = useCallback(
    async (newReason: string | null) => {
      if (!location.knockStatusHistoryId) return false
      setIsSavingReason(true)
      try {
        // Backend EntityType is a strict enum (Lead/Opportunity/DraftLead/
        // KnockStatus). location.entityType is always "PestPacLocation" here
        // (hardcoded on the backend's pin mapping) - not a valid enum member -
        // so this must always be 'KnockStatus', the value for a knock status
        // not tied to a real Lead/Opp/DraftLead record.
        const entityType = 'KnockStatus'
        await dispatch(
          updateKnockStatusHistoryById({
            id: location.knockStatusHistoryId,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId: location.entityId ?? 0,
            },
          }),
        ).unwrap()
        toast.success('Reason updated')
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingReason(false)
      }
    },
    [location.knockStatusHistoryId, location.entityId, dispatch],
  )

  const nameAndCompanyDisplay = useMemo(
    () =>
      getPestPacDisplayName(
        details?.firstName,
        details?.lastName,
        details?.company,
      ),
    [details?.company, details?.firstName, details?.lastName],
  )

  const pestPacLocationUrl = useGetLocationUrl(details?.locationId ?? '', false)

  const onNameClick = useCallback(() => {
    if (pestPacLocationUrl) {
      window.open(pestPacLocationUrl, '_blank', 'noopener,noreferrer')
    }
  }, [pestPacLocationUrl])

  const address = location.formattedAddress ?? ''

  const onAddressMapsClick = useCallback(() => {
    if (!address) return
    const url = googleMapsOpenInMapsLink(address)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [address])

  const phone =
    formatUSPhoneNumber(details?.phone ?? '').trim() || details?.phone || ''

  const onPhoneNumberClick = useCallback(() => {
    const telHref = getTelHref(phone)
    if (telHref) {
      window.open(telHref, '_blank', 'noopener,noreferrer')
    }
  }, [phone])

  const hasKnockStatus =
    !!location.knockStatusSettingName &&
    location.knockStatusSettingName !== 'None' &&
    knockStatusLocationId !== null

  // SH-1781: cleared reflects this session's clear before the tile refetch lands.
  const hasActiveStatus = hasKnockStatus && !cleared

  const knockStatus = useMemo(() => {
    return {
      id: location.knockStatusHistoryId ?? null,
      isDefault: location.knockStatusSettingName === 'Do not knock',
      status: location.knockStatusSettingName || 'None',
      note: location.knockStatusReason || null,
      createdTime: location.knockStatusCreatedTime || null,
      createdByName: location.knockStatusCreatedByName || null,
    }
  }, [
    location.knockStatusHistoryId,
    location.knockStatusCreatedByName,
    location.knockStatusCreatedTime,
    location.knockStatusReason,
    location.knockStatusSettingName,
  ])

  // A cleared PestPac pin loses its knock-status fields on the tile, but the location keeps its
  // history -- resolve it through the PestPac detail lookup the "View >" flow already uses, which
  // is PestPac-aware and still resolves once the status is cleared (SH-1816).
  const [clearedHistory, setClearedHistory] =
    useState<PestPacHistoryLink | null>(null)

  useEffect(() => {
    // Optimistic fallback: `cleared` still has the pre-refresh location id/count
    // on hand, so show the link with those right away instead of leaving it
    // absent for the lookup's whole round-trip (Copilot review, SH-1781).
    setClearedHistory(
      cleared && knockStatusLocationId !== null
        ? { knockStatusLocationId, count: location.knockStatusCount ?? 0 }
        : null,
    )
    // StatusSection hides the whole card without D2D, so there's nothing to resolve for.
    if (!isD2dEnabled || hasActiveStatus || !pestPacLocationId) return

    let active = true
    getPestPacHistoryLink(pestPacLocationId)
      .then((link) => {
        // Always apply the resolved value, even null, so a confirmed "no
        // history" replaces the optimistic guess rather than leaving it stuck.
        if (active) setClearedHistory(link)
      })
      .catch((error) => {
        // Log rather than swallow: a failure here is indistinguishable from "no history", so the
        // link silently goes missing and this is the only trace of why.
        console.error(
          '[MapBubbleContentsPestPac] Failed to resolve cleared knock status history:',
          error,
        )
      })

    return () => {
      active = false
    }
  }, [
    isD2dEnabled,
    hasActiveStatus,
    pestPacLocationId,
    cleared,
    knockStatusLocationId,
    location.knockStatusCount,
  ])

  const historyLink = resolveKnockStatusHistoryLink({
    hasActiveStatus,
    activeLocationId: knockStatusLocationId,
    activeCount: location.knockStatusCount,
    cleared: clearedHistory,
  })

  const handleViewKnockStatusHistory = useCallback(() => {
    if (historyLink.locationId !== null)
      onViewKnockStatusHistory?.(historyLink.locationId)
  }, [historyLink.locationId, onViewKnockStatusHistory])

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">PestPac Account</Typography>
        {showCloseBtn && (
          <IconButton
            onClick={onClose}
            size={'small'}
            sx={{
              cursor: 'pointer',
              mr: '-10px',
            }}
            data-testid="action-modal-close-btn"
            data-test-id="mapBubblePestPacCloseBtn"
          >
            <CloseIcon fontSize={'small'} />
          </IconButton>
        )}
      </Stack>

      {nameAndCompanyDisplay ? (
        pestPacLocationUrl ? (
          <Link
            onClick={onNameClick}
            sx={{
              cursor: 'pointer',
              display: 'block',
              maxWidth: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#1565C0' }}>
              {nameAndCompanyDisplay}
            </Typography>
          </Link>
        ) : (
          <Typography variant="h6" sx={{ color: '#1565C0' }}>
            {nameAndCompanyDisplay}
          </Typography>
        )
      ) : null}

      {address ? (
        <Link
          component="button"
          type="button"
          onClick={onAddressMapsClick}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: 0,
            mt: 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              '&:hover': { color: 'rgba(25, 112, 211, 0.9)' },
            }}
          >
            {address}
          </Typography>
          <LaunchIcon
            sx={{
              marginLeft: (theme: Theme) => theme.spacing(0.5),
              fontSize: '18px',
              color: '#1565C0',
              flexShrink: 0,
            }}
          />
        </Link>
      ) : null}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {phone ? (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500',
              alignContent: 'center',
            }}
            variant="subtitle4"
          >
            Phone:
            <Button
              color="primary"
              variant="text"
              size="small"
              onClick={onPhoneNumberClick}
              sx={{
                width: 'fit-content',
                color: '#1565C0',
              }}
              disableElevation
            >
              {phone}
            </Button>
          </Typography>
        ) : null}

        {details?.locationId != null && details?.locationCode ? (
          <LocationLink
            externalLocationId={String(details.locationId)}
            externalLocationCode={String(details.locationCode)}
            billingLocation={false}
            typographyVariant="subtitle4"
            fontSize="14px"
            containerSx={{
              alignContent: 'center',
            }}
            linkButtonSx={{
              minWidth: 'fit-content',
              maxWidth: 'fit-content',
              padding: 0.5,
            }}
          />
        ) : null}
      </Stack>

      <StatusSection
        knockStatus={applyClearedOverride(knockStatus, cleared)}
        clearButton={
          isD2dEnabled && hasActiveStatus && canEdit ? (
            <ClearKnockStatusButton onClick={openClearConfirm} />
          ) : undefined
        }
        viewHistoryButton={
          historyLink.canView &&
          onViewKnockStatusHistory && (
            <ViewKnockStatusHistoryButton
              onClick={handleViewKnockStatusHistory}
            />
          )
        }
        canEdit={canEdit}
        isSavingReason={isSavingReason}
        onSaveReason={handleSaveReason}
      />

      <Box mt={1.6}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 0.8,
          }}
        >
          {isD2dEnabled && onKnockStatusClick ? (
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: '#D4E3F3',
              }}
              onClick={onKnockStatusClick}
              color="secondary"
            >
              Knock Status
            </Button>
          ) : null}

          {details?.locationId && onViewClick ? (
            <Button fullWidth variant="contained" onClick={onViewClick}>
              {viewLabel}
              {showViewChevron ? (
                <ChevronRight sx={{ fontSize: '20px', ml: '8px', mt: '1px' }} />
              ) : null}
            </Button>
          ) : null}
        </Box>

        {canAddLead && onAddLeadClick ? (
          <Button
            fullWidth
            variant="contained"
            onClick={onAddLeadClick}
            sx={{ mt: 0.8 }}
          >
            Add Lead
          </Button>
        ) : null}
      </Box>

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />
    </>
  )
}
